import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import * as S from '../styles';

import OutboxIcon from '@mui/icons-material/Outbox';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { ISubjectContentData } from '../../../..';
import { useTheme } from 'styled-components';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../../ProviderNewStudent';
import {
  IContentSubject,
  IContentSubjectGraduation,
  IDataContentSubject,
  myCoursesServiceNewStudent,
} from '../../../../../../../../services/newStudent/myCourses/myCourses.service';
import { IClassInfo } from '../../../../../../../../pages/newStudent/MyCoursesNewStudent/components/Subjects';

import Modal from '@mui/material/Modal';
import { Box } from '@mui/material';

interface IIdShouldDisplay {
  video?: number;
  archive?: number;
  audiobook?: number;
  customized?: number;
  tcc?: number;
  externalLink?: number;
  internship?: number;
  supplementaryMaterial?: number;
  assessmentWork?: number;
}
interface Props {
  setSubjectContent?: Dispatch<SetStateAction<ISubjectContentData | undefined>>;
  classInfo: IClassInfo;
  contentIdSelected: number;
  setBlockedContent: Dispatch<SetStateAction<boolean>>;
}
export default function TabLesson({
  setSubjectContent,
  classInfo,
  contentIdSelected,
  setBlockedContent,
}: Props) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const theme = useTheme();
  const [content, setContent] = useState<
    IContentSubject | IContentSubjectGraduation[]
  >();
  const [contentShouldDisplay, setContentShouldDisplay] =
    useState<IIdShouldDisplay>();
  const [contentOpen, setContentOpen] = useState<number>();
  const [unitOpen, setUnitOpen] = useState<number>();
  const [classOpen, setClassOpen] = useState<number>();

  const courseId = classInfo.courseId;
  const disciplineId = classInfo.id;
  const contentId = contentIdSelected;

  const [openSupplementaryModal, setOpenSupplementaryModal] = useState(false);
  const [modalItem, setModalItem] = useState<IDataContentSubject | null>(null);

  const handleOpenSupplementaryModal = (item) => {
    setModalItem(item);
    setOpenSupplementaryModal(true);
  };

  const handleCloseSupplementaryModal = () => {
    setOpenSupplementaryModal(false);
    setModalItem(null);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);

        const { result, data } = await myCoursesServiceNewStudent.getContent(
          courseId,
          disciplineId,
          contentId,
        );

        if (result) {
          setContent(data);
          setBlockedContent(false);

          const displayFiltered: IIdShouldDisplay = {};
          let count = 0;

          for (const key in data) {
            if (Object.hasOwnProperty.call(data, key)) {
              count = count + 1;
              if (data[key].length > 0) {
                displayFiltered[key] = count;
              }
            }
          }

          setContentShouldDisplay(displayFiltered);
        }
      } catch (error) {
        throw new Error(error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [courseId, disciplineId, contentId]);

  const itemInfoCollapse = (
    item: IDataContentSubject,
    index: number,
    type:
      | 'video'
      | 'archive'
      | 'audiobook'
      | 'customized'
      | 'externalLink'
      | 'tcc'
      | 'internship'
      | 'supplementaryMaterial'
      | 'assessmentWork',
    icon: ReactNode,
  ) => {
    return (
      <S.DivSubtitle key={index}>
        <S.DivSubtitleSeparated>
          {icon}
          <S.Subtitle
            onClick={() => {
              if (type === 'supplementaryMaterial') {
                handleOpenSupplementaryModal(item);
              } else if (type === 'externalLink') {
                window.open(item.url, '_blank', 'noopener,noreferrer');
              } else {
                setSubjectContent!({
                  type: type,
                  url: item.url ?? '',
                  customized: item.customized,
                  title: item.title,
                  contentId: contentIdSelected,
                  resourceId: item.resourceId,
                });
              }
            }}
          >
            {item.title}
          </S.Subtitle>
        </S.DivSubtitleSeparated>
      </S.DivSubtitle>
    );
  };

  const unitTitleCollapse = (contentShouldDisplay, title) => {
    return (
      <S.DitTitle>
        {unitOpen !== contentShouldDisplay ? (
          <ArrowDropDownIcon fontSize="large" />
        ) : (
          <ArrowDropUpIcon fontSize="large" />
        )}
        <S.TitleName
          onClick={() => {
            unitOpen !== contentShouldDisplay
              ? setUnitOpen(contentShouldDisplay)
              : setUnitOpen(undefined);
          }}
        >
          {title}
        </S.TitleName>
      </S.DitTitle>
    );
  };

  const classTitleCollapse = (contentShouldDisplay, title) => {
    return (
      <S.DitTitle style={{ paddingLeft: '10px' }}>
        {classOpen !== contentShouldDisplay ? (
          <ArrowDropDownIcon fontSize="large" />
        ) : (
          <ArrowDropUpIcon fontSize="large" />
        )}
        <S.TitleName
          onClick={() => {
            classOpen !== contentShouldDisplay
              ? setClassOpen(contentShouldDisplay)
              : setClassOpen(undefined);
          }}
        >
          {title}
        </S.TitleName>
      </S.DitTitle>
    );
  };

  const itemTitleCollapse = (contentShouldDisplay, title) => {
    return (
      <S.DitTitle style={{ paddingLeft: '10px' }}>
        {contentOpen !== contentShouldDisplay ? (
          <ArrowDropDownIcon fontSize="large" />
        ) : (
          <ArrowDropUpIcon fontSize="large" />
        )}
        <S.TitleName
          onClick={() => {
            contentOpen !== contentShouldDisplay
              ? setContentOpen(contentShouldDisplay)
              : setContentOpen(undefined);
          }}
        >
          {title}
        </S.TitleName>
      </S.DitTitle>
    );
  };

  return (
    <>
      {openSupplementaryModal && modalItem && (
        <Modal
          open={openSupplementaryModal}
          onClose={handleCloseSupplementaryModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100vw',
              height: '100vh',
              bgcolor: 'background.paper',
              outline: 'none',
              p: 0,
              m: 0,
            }}
          >
            <S.ButtonClose onClick={handleCloseSupplementaryModal}>
              Fechar
            </S.ButtonClose>

            <iframe
              src={modalItem?.url}
              width="100%"
              height="100%"
              allow="autoplay"
              style={{ border: 'none' }}
            />
          </Box>
        </Modal>
      )}

      <S.Container>
        {content && Array.isArray(content) ? (
          content?.map((unit, index) => (
            <S.ContentDiv key={index}>
              {unitTitleCollapse(index, unit.unitName)}
              {unitOpen === index &&
                unit.class?.map((cls, index2) => (
                  <S.ContentDiv key={index2}>
                    {classTitleCollapse(index2, cls.classroomName)}
                    {classOpen === index2 && (
                      <S.ContentDiv style={{ paddingLeft: '10px' }}>
                        {cls.resources?.video?.length ? (
                          <>
                            {itemTitleCollapse(0, 'Vídeos')}
                            {contentOpen === 0 &&
                              cls.resources?.video?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`video-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'video',
                                      <OndemandVideoOutlinedIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.archive?.length ? (
                          <>
                            {itemTitleCollapse(1, 'Apostila')}
                            {contentOpen === 1 &&
                              cls.resources?.archive?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`archive-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'archive',
                                      <DescriptionOutlinedIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.audiobook?.length ? (
                          <>
                            {itemTitleCollapse(2, 'AudioBook')}
                            {contentOpen === 2 &&
                              cls.resources?.audiobook?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`audiobook-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'audiobook',
                                      <GraphicEqIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.customized?.length ? (
                          <>
                            {itemTitleCollapse(3, 'Conteúdo')}
                            {contentOpen === 3 &&
                              cls.resources?.customized?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`customized-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'customized',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.externalLink?.length ? (
                          <>
                            {itemTitleCollapse(4, 'Externo')}
                            {contentOpen === 4 &&
                              cls.resources?.externalLink?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`externalLink-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'externalLink',
                                      <OpenInNewOutlinedIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.supplementaryMaterial?.length ? (
                          <>
                            {itemTitleCollapse(5, 'Material')}
                            {contentOpen === 5 &&
                              cls.resources?.supplementaryMaterial?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`supplementaryMaterial-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'supplementaryMaterial',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.internship?.length ? (
                          <>
                            {itemTitleCollapse(6, 'Estágio')}
                            {contentOpen === 6 &&
                              cls.resources?.internship?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`internship-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'internship',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.tcc?.length ? (
                          <>
                            {itemTitleCollapse(7, 'Estágio')}
                            {contentOpen === 7 &&
                              cls.resources?.tcc?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`tcc-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'tcc',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                        {cls.resources?.assessmentWork?.length ? (
                          <>
                            {itemTitleCollapse(8, 'Trabalho Avaliativo')}
                            {contentOpen === 8 &&
                              cls.resources?.assessmentWork?.map(
                                (item: IDataContentSubject, index: number) => (
                                  <S.ContentDiv
                                    key={`assessmentWork-${index}`}
                                    style={{ paddingLeft: '30px' }}
                                  >
                                    {itemInfoCollapse(
                                      item,
                                      index,
                                      'assessmentWork',
                                      <OutboxIcon
                                        fontSize="large"
                                        style={{
                                          color: theme.colors?.text.danger,
                                        }}
                                      />,
                                    )}
                                  </S.ContentDiv>
                                ),
                              )}
                          </>
                        ) : null}
                      </S.ContentDiv>
                    )}
                  </S.ContentDiv>
                ))}
            </S.ContentDiv>
          ))
        ) : (
          <>
            {contentShouldDisplay?.video && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.video, 'Vídeos')}
                {contentOpen === contentShouldDisplay?.video &&
                  content?.video?.map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'video',
                        <OndemandVideoOutlinedIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.archive && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.archive, 'Apostila')}
                {contentOpen === contentShouldDisplay?.archive &&
                  content?.archive?.map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'archive',
                        <DescriptionOutlinedIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.audiobook && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.audiobook, 'AudioBook')}
                {contentOpen === contentShouldDisplay?.audiobook &&
                  content?.audiobook?.map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'audiobook',
                        <GraphicEqIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.customized && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.customized, 'Conteúdo')}
                {contentOpen === contentShouldDisplay?.customized &&
                  content?.customized?.map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'customized',
                        <GraphicEqIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.internship && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.internship, 'Estágio')}
                {contentOpen === contentShouldDisplay?.internship &&
                  content?.internship?.map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'internship',
                        <OutboxIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.tcc && (
              <S.ContentDiv>
                {itemTitleCollapse(contentShouldDisplay.tcc, 'TCC')}
                {contentOpen === contentShouldDisplay?.tcc &&
                  content?.tcc?.map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'tcc',
                        <OutboxIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
            {contentShouldDisplay?.supplementaryMaterial && (
              <S.ContentDiv>
                {itemTitleCollapse(
                  contentShouldDisplay.supplementaryMaterial,
                  'Material',
                )}
                {contentOpen === contentShouldDisplay?.supplementaryMaterial &&
                  content?.supplementaryMaterial?.map(
                    (item: IDataContentSubject, index: number) =>
                      itemInfoCollapse(
                        item,
                        index,
                        'supplementaryMaterial',
                        <OutboxIcon
                          fontSize="large"
                          style={{
                            color: theme.colors?.text.danger,
                          }}
                        />,
                      ),
                  )}
              </S.ContentDiv>
            )}
          </>
        )}
      </S.Container>
    </>
  );
}
